import { getOrderedTemplates } from 'components/TemplateManager/ManagerModal';
import { STRINGS } from 'config';
import _find from 'lodash/find';
import { mapTemplate } from 'store/action/templates';

export type visibilityTypes = 'SHARED' | 'PERSONAL' | 'GLOBAL';
export const NAME_MIN_LENGTH = 3;
export const NAME_MAX_LENGTH = 200;
export const CONTENT_MIN_LENGTH = 1;
export const CONTENT_MAX_LENGTH = 1000;
export const PRACTICE_ROLE_MIN_LENGTH = 1;
export const PRACTICE_ROLE_MAX_LENGTH = 100;

// Define a type for API error structure
type ApiErrors = {
  isDuplicateName?: boolean;
};

export const getNameErrorMessage = (
  name: string,
  apiErrors: ApiErrors,
  templateType: string,
  showValidation: boolean,
): string | boolean => {
  if (showValidation && name === '') {
    return 'No name entered';
  }
  if (apiErrors.isDuplicateName) {
    return `Template name already exists in ${templateType.toLowerCase()} templates`;
  }
  if (name && name.length < NAME_MIN_LENGTH) {
    return `Template name must be ${NAME_MIN_LENGTH} or more characters`;
  }
  if (name.length > NAME_MAX_LENGTH) {
    return `Template name must be ${NAME_MAX_LENGTH} characters or less`;
  }
  if (name === 'Default template' || name === 'Emis Default template') {
    return 'Template name already exists in shared templates';
  }
  return false;
};

export const getRoleErrorMessage = (roleLength: number): string => {
  if (roleLength < PRACTICE_ROLE_MIN_LENGTH) {
    return `Template role must be ${PRACTICE_ROLE_MIN_LENGTH} or more characters`;
  }
  if (roleLength > PRACTICE_ROLE_MAX_LENGTH) {
    return `Template role must be ${PRACTICE_ROLE_MAX_LENGTH} characters or less`;
  }
  return '';
};

export const getContentErrorMessage = (contentLength: number): string => {
  if (contentLength < CONTENT_MIN_LENGTH) {
    return 'Please enter a message';
  }
  if (contentLength > CONTENT_MAX_LENGTH) {
    return `Template content must have ${CONTENT_MAX_LENGTH} characters or less`;
  }
  return '';
};

export interface TemplateBody {
  id?: string | null;
  name: string;
  sectionHeadings: string[];
  visibility: visibilityTypes | null;
}
export interface Template {
  id: string | null;
  name: string;
  practiceRole: string;
  content: string;
  type: string;
  transcriptionType: 'Consultation' | 'Dictation';
  isNewTemplate?: boolean;
}
export interface TemplatesState {
  templatesLoadingState: string;
  showTemplateValidation: boolean;
  templateSavingState: string;
  apiErrors: { isDuplicateName: boolean };
  template: Template;
  templates: Template[];
  isTemplateSaving: boolean;
  isTemplateDeleting: boolean;
  isSaveTemplateDialogOpen: boolean;
  isNewMessageSaveTemplateDialogOpen: boolean;
  templateFilter: string | null;
  templateTitle: string;
  templateHasUnsavedChanges: boolean;
}

type TemplatesAction = {
  type: string;
  payload?: unknown;
};
const { PENDING, IDLE } = STRINGS.REQUEST_STATUS;

export const initialTemplatesState = {
  templatesLoadingState: IDLE,
  showTemplateValidation: false,
  templateSavingState: IDLE,
  apiErrors: { isDuplicateName: false },
  template: {
    id: null,
    name: '',
    content: '',
    type: STRINGS.TEMPLATE.TEMPLATE_TYPE.PERSONAL,
    isNewTemplate: false,
    practiceRole: '',
    transcriptionType: 'Consultation' as const,
  },
  templates: [],
  isTemplateSaving: false,
  isTemplateDeleting: false,
  isSaveTemplateDialogOpen: false,
  isNewMessageSaveTemplateDialogOpen: false,
  templateFilter: null,
  templateTitle: '',
  templateHasUnsavedChanges: false,
};

export const templatesReducer = (
  state: TemplatesState = initialTemplatesState,
  action: TemplatesAction,
): TemplatesState => {
  switch (action.type) {
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_REQUEST:
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_REQUEST:
      return { ...state, templateSavingState: PENDING, apiErrors: initialTemplatesState.apiErrors };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_SUCCESS:
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_SUCCESS: {
      const payload = action.payload as {
        id: string;
        name: string;
        practiceRole: string;
        sectionHeadings?: string[];
        visibility: string;
      };
      const mappedTemplate = mapTemplate(payload);
      const updatedTemplates = [...state.templates.filter(t => t.id !== payload.id), mappedTemplate];
      const templates = getOrderedTemplates(updatedTemplates);
      return {
        ...state,
        templateSavingState: IDLE,
        showTemplateValidation: false,
        template: { ...mappedTemplate },
        templateHasUnsavedChanges: false,
        templates,
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.ADD_NEW_TEMPLATE_ERROR:
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_ERROR: {
      const payload = action.payload as { id: string; apiErrors: { isDuplicateName: boolean } };
      return {
        ...state,
        templateSavingState: IDLE,
        ...(payload.id === state.template.id
          ? { showTemplateValidation: true, apiErrors: { ...state.apiErrors, ...payload.apiErrors } }
          : {}),
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SELECT_TEMPLATE:
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SELECT_TEMPLATE_IN_TEMPLATE_MANAGER:
      return {
        ...state,
        template: action.payload as Template,
        showTemplateValidation: initialTemplatesState.showTemplateValidation,
        apiErrors: initialTemplatesState.apiErrors,
        templateSavingState: initialTemplatesState.templateSavingState,
        templateHasUnsavedChanges: initialTemplatesState.templateHasUnsavedChanges,
      };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.CLEAR_TEMPLATE_IN_TEMPLATE_MANAGER: {
      const originalTemplate = _find(state.templates, { id: state.template.id }) || initialTemplatesState.template;
      const template = state.template.id === 'newTemplate' ? state.templates[0] : originalTemplate;
      return {
        ...state,
        showTemplateValidation: initialTemplatesState.showTemplateValidation,
        apiErrors: initialTemplatesState.apiErrors,
        templateSavingState: initialTemplatesState.templateSavingState,
        templateHasUnsavedChanges: false,
        template,
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_NAME: {
      const payload = action.payload as string;
      const originalTemplate = _find(state.templates, { id: state.template.id });
      const templateHasUnsavedChanges = originalTemplate?.name !== payload;
      return { ...state, template: { ...state.template, name: payload }, templateHasUnsavedChanges };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_PRACTICE_ROLE: {
      const payload = action.payload as string;
      const originalTemplate = _find(state.templates, { id: state.template.id });
      const templateHasUnsavedChanges = originalTemplate?.practiceRole !== payload;
      return { ...state, template: { ...state.template, practiceRole: payload }, templateHasUnsavedChanges };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_TRANSCRIPTION_TYPE: {
      const payload = action.payload as 'Consultation' | 'Dictation';
      const originalTemplate = _find(state.templates, { id: state.template.id });
      const templateHasUnsavedChanges = originalTemplate?.transcriptionType !== payload;
      return { ...state, template: { ...state.template, transcriptionType: payload }, templateHasUnsavedChanges };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_CONTENT: {
      const payload = action.payload as string;
      const originalTemplate = _find(state.templates, { id: state.template.id });
      const templateHasUnsavedChanges = originalTemplate?.content !== payload;
      return {
        ...state,
        template: { ...state.template, content: payload },
        templateHasUnsavedChanges,
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_TYPE: {
      const payload = action.payload as string;
      return {
        ...state,
        template: { ...state.template, type: payload },
        apiErrors: { ...state.apiErrors, isDuplicateName: false },
        templateHasUnsavedChanges: true,
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SET_SHOW_TEMPLATE_VALIDATION: {
      const payload = action.payload as boolean;
      return { ...state, showTemplateValidation: payload, apiErrors: initialTemplatesState.apiErrors };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.GET_TEMPLATES_REQUEST:
      return { ...state, templatesLoadingState: PENDING };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.GET_TEMPLATES_SUCCESS: {
      const payload = action.payload as Template[];
      const sortedTemplates = getOrderedTemplates(payload);
      return {
        ...state,
        templates: sortedTemplates,
        templatesLoadingState: IDLE,
        template: sortedTemplates[0],
      };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.GET_TEMPLATES_ERROR:
      return { ...state, templatesLoadingState: IDLE };
    // case STRINGS.REDUCERS.WORKFLOW_REDUCER.CLEAR_SESSION:
    //   return { ...initialTemplatesState, templates: state.templates };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SAVE_TEMPLATE_REQUEST:
      return { ...state, isTemplateSaving: true };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SAVE_TEMPLATE_SUCCESS:
      return { ...state, isTemplateSaving: false };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.SAVE_TEMPLATE_ERROR:
      return { ...state, isTemplateSaving: false };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.DELETE_TEMPLATE_REQUEST:
      return { ...state, isTemplateDeleting: true };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.DELETE_TEMPLATE_SUCCESS: {
      const payload = action.payload as string;
      const templates = state.templates.filter(t => t.id !== payload);
      return { ...state, isTemplateDeleting: false, templates, template: templates[0] };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.DELETE_TEMPLATE_ERROR:
      return { ...state, isTemplateDeleting: false };
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.TOGGLE_SAVE_TEMPLATE_DIALOG: {
      const payload = action.payload as boolean;
      return { ...state, isSaveTemplateDialogOpen: payload };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.TOGGLE_NEW_MESSAGE_SAVE_TEMPLATE_DIALOG: {
      const payload = action.payload as boolean;
      return { ...state, isNewMessageSaveTemplateDialogOpen: payload };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_FILTER: {
      const payload = action.payload as string | null;
      return { ...state, templateFilter: payload };
    }
    case STRINGS.REDUCERS.TEMPLATES_REDUCER.UPDATE_TEMPLATE_TITLE: {
      const payload = action.payload as string;
      return { ...state, templateTitle: payload };
    }
    default:
      return state;
  }
};

export default templatesReducer;
